import React, { useEffect, createContext, useState, useMemo, useContext } from 'react';
import { useParams } from 'react-router';
import { ADD_NOTES, ORDER_BY_ID, UPDATE_ORDER_BY_ID } from './graphql';
import { UPSERT_ORDERS } from '@/components/ShipmentForm/graphql/mutations';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { GET_PRICING_OVERRIDE_BY_ID } from '@/graphql/queries/pricing_overrides';
import { useClientUser } from '@/hooks';
import { UserContext } from '../App';
import { ACCESSORIALS_BY_ALGO, genAccessorials } from '@onward-delivery/core';
import { isFinite, pick } from 'lodash';
import { post } from '@/utilities/onwardClient';
import { mergeTags } from '@/utilities/mergeTags';
import { PRICING_LTL } from '@/constants/apiRoutes';
import { FIXED_CHARGES, PERSPECTIVE_MAP, PRICING_FIELDS } from './constants';
import { QUERY_ADMIN_TAGS } from '../admin/AdminTags/graphql';
import { ASSET_HEAVY } from '@/constants/featureFlags';
export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { order_id, perspective } = useParams();
    const [notification, setNotification] = useState({});
    const [updates, setUpdates] = useState({});
    const [newNote, setNewNote] = useState('');
    const [newCharge, setNewCharge] = useState(null);
    const [pricingInFlight, setPricingInFlight] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [baseChargeComment, setBaseChargeComment] = useState('');
    const { user, client, teammate, org_tags } = useContext(UserContext);

    const [upsertOrder, { loading: upsertOrderLoading }] = useMutation(UPSERT_ORDERS, {
        refetchQueries: [ORDER_BY_ID],
        awaitRefetchQueries: true,
        onError: (error) => {
            Sentry.captureException(error);
            console.error(error);
            setNotification({ message: 'Failed to update order. Please try again.', type: 'error' });
        },
    });
    const { client_id, teammateRoles } = useClientUser();
    const PERSPECTIVE = PERSPECTIVE_MAP[perspective || 'internal'];

    const [fetchOverride, { data: overrideData, loading: overrideInFlight }] = useLazyQuery(
        GET_PRICING_OVERRIDE_BY_ID,
        {
            onError: (err) => {
                console.error(err);
                Sentry.captureException(err);
            },
        }
    );

    const [insertNotes, { loading: notesInFlight }] = useMutation(ADD_NOTES, {
        onError: (err) => {
            setNotification({
                message: 'Failed to add notes',
                type: 'error',
            });
            console.error(err);
            Sentry.captureException(err);
        },
        update: (cache, { data: { insert_notes } }) => {
            cache.updateQuery(
                {
                    query: ORDER_BY_ID,
                    variables: {
                        order_id: order_id,
                    },
                },
                (data) => {
                    const cloned = data?.orders?.[0]?.notes || [];

                    return {
                        ...data,
                        orders: [{ ...data?.orders?.[0], notes: [...cloned, ...insert_notes.returning] }],
                    };
                }
            );
        },
    });

    const [updateOrder, { loading: updateInFlight }] = useMutation(UPDATE_ORDER_BY_ID, {
        onCompleted: () => {
            setNotification({
                message: 'Order updated successfully',
                type: 'success',
            });
        },
        onError: (err) => {
            setNotification({
                message: 'Failed to update order',
                type: 'error',
            });
            console.error(err);
            Sentry.captureException(err);
        },
    });

    const { data, loading: orderInFlight } = useQuery(ORDER_BY_ID, {
        variables: {
            order_id: order_id,
        },
        onError: (err) => {
            setNotification({
                message: 'Failed to load order',
                type: 'error',
            });
            console.error(err);
            Sentry.captureException(err);
        },
    });

    const order = useMemo(() => {
        return data?.orders?.[0];
    }, [data]);

    const updated = useMemo(() => {
        return {
            ...(order || {}),
            ...(updates || {}),
        };
    }, [order, updates]);

    const invoicingNotes = useMemo(() => {
        return updated?.notes?.filter((note) => note.type === 'Invoicing' && !note.private_to);
    }, [updated]);

    const { data: tagsData } = useQuery(QUERY_ADMIN_TAGS, { variables: {} });

    const marketplaceTags = useMemo(() => {
        return [...(tagsData?.results || [])];
    }, [tagsData]);

    const qboTags = useMemo(() => {
        return updated?.[PERSPECTIVE.client]?.qbo_tags || [];
    }, [updated, PERSPECTIVE]);

    const editable = useMemo(() => {
        if (!order) {
            return false;
        }
        const isAdmin = ['ADMIN', 'ONWARD_ADMIN'].some((role) => user?.roles?.[role]);
        const isCarrierAdmin =
            client?.user?.roles?.['CARRIER'] &&
            client?.user?.circles?.[ASSET_HEAVY] &&
            (!teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role]));
        const isClient = user?.roles?.[PERSPECTIVE.role] && client_id === order[PERSPECTIVE.id];
        const isInvoicingTeammate =
            user?.roles?.['TEAMMATE'] && client_id === order[PERSPECTIVE.id] && teammateRoles?.invoicing;
        const isEditableWithInvoice = PERSPECTIVE.editable_with_invoice || !order[PERSPECTIVE.invoice_id];
        return isAdmin || isCarrierAdmin || ((isClient || isInvoicingTeammate) && isEditableWithInvoice);
    }, [order, user, client_id, PERSPECTIVE, teammateRoles, client, teammate]);

    const isInvoiced = useMemo(() => {
        return !!order?.[PERSPECTIVE.invoice_id];
    }, [order, PERSPECTIVE]);

    useEffect(() => {
        const pricing_override_id = order?.[PERSPECTIVE.pricing_override_id];
        if (pricing_override_id) {
            fetchOverride({ variables: { pricing_override_id } });
        }
    }, [order, PERSPECTIVE]);

    const rates = useMemo(() => {
        const rates = overrideData?.pricing_overrides_by_pk?.tariff?.rates || [];
        const grouped = (rates || []).reduce((acc, rate) => {
            const next = [...(acc[rate.type] || []), rate];
            acc[rate.type] = next.sort((l, r) => {
                if (l.min === r.min) {
                    return l.min2 - r.min2;
                }
                return l.min - r.min;
            });
            return acc;
        }, {});
        return grouped;
    }, [overrideData]);

    const options = useMemo(() => {
        const algoType = order?.price_breakdown?.[PERSPECTIVE.breakdown]?._meta?.type || 'DEFAULT';
        // The order's client tags merged with org tags it inherits
        // dedupe on tag name preferring client tags
        const clientTags = mergeTags(order?.[PERSPECTIVE.client]?.tags, org_tags);
        return [
            { rate: 0, quantity: 1, total: 0, type: 'CUSTOM_ACCESSORIAL', label: 'Custom' },
            ...genAccessorials(algoType, [...clientTags, ...marketplaceTags])
                .map((accessorial) => {
                    const enrichments = rates[accessorial.type] || rates[`${accessorial.type}`];
                    const enriched = {
                        ...accessorial,
                        qbo_tag: enrichments?.[0]?.qbo_tag || '',
                        rate: enrichments?.[0]?.rate || 0,
                        quantity: 1,
                        total: enrichments?.[0]?.rate || 0,
                    };
                    return enriched;
                })
                .sort((a, b) => {
                    if (a.qbo_tag && !b.qbo_tag) {
                        return -1;
                    }
                    if (!a.qbo_tag && b.qbo_tag) {
                        return 1;
                    }
                    return a.label.localeCompare(b.label);
                }),
        ];
    }, [order, PERSPECTIVE, rates, marketplaceTags, org_tags]);

    const [baseCharge, accessorials] = useMemo(() => {
        const algoType = order?.price_breakdown?.[PERSPECTIVE.breakdown]?._meta?.type || 'DEFAULT';
        let base = 0;
        let accessorials = [];
        if (isFinite(updates[PERSPECTIVE.baseOverride])) {
            base = updates[PERSPECTIVE.baseOverride];
            accessorials = updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides] || [];
        } else {
            base = FIXED_CHARGES.reduce((acc, attr) => {
                return acc + (order?.price_breakdown?.[PERSPECTIVE.breakdown]?.[attr] || 0);
            }, 0);
            accessorials =
                updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides] ||
                order?.price_breakdown?.[PERSPECTIVE.breakdown]?.accessorials ||
                [];
        }

        const breakdown = order?.price_breakdown?.[PERSPECTIVE.breakdown] || {};
        return [
            {
                type: 'BASE_CHARGE',
                rate: base,
                quantity: 1,
                total: base,
                label: 'Base Charge',
                qbo_tag: breakdown.base_charge_qbo_tag || '',
                comment: baseChargeComment || breakdown.base_charge_comment || '',
                created_by: breakdown.base_charge_created_by || '',
                created_at: breakdown.base_charge_created_at || '',
                last_edited_by: breakdown.base_charge_last_edited_by || '',
                last_edited_at: breakdown.base_charge_last_edited_at || '',
            },
            accessorials.map((accessorial) => ({
                label: ACCESSORIALS_BY_ALGO[algoType]?.[accessorial.type]?.label || '',
                ...accessorial,
                total: accessorial.quantity * accessorial.rate,
            })),
        ];
    }, [order, updated, updates, PERSPECTIVE, baseChargeComment]);

    const [subtotal, accessorialsSum, total] = useMemo(() => {
        const subtotal = baseCharge?.total || 0;
        const accessorialsSum = accessorials.reduce((acc, accessorial) => {
            return acc + (accessorial.total || 0);
        }, 0);
        const total = subtotal + accessorialsSum;
        return [subtotal, accessorialsSum, total];
    }, [baseCharge, accessorials]);

    const isBaseChargeEdited = useMemo(() => {
        return isFinite(updates[PERSPECTIVE.baseOverride]) || !!baseChargeComment;
    }, [updates, PERSPECTIVE, baseChargeComment]);

    const isAccessorialsEdited = useMemo(() => {
        return !!updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides];
    }, [updated, PERSPECTIVE]);

    const hasCommentErrors = useMemo(() => {
        if (isBaseChargeEdited && !baseCharge?.comment) return true;
        if (isAccessorialsEdited) {
            return accessorials.some((a) => !a.is_auto_applied && !a.comment);
        }
        return false;
    }, [baseCharge, accessorials, isBaseChargeEdited, isAccessorialsEdited]);

    return (
        <Context.Provider
            value={{
                state: {
                    updated,
                    editable,
                    isInvoiced,
                    qboTags,
                    rates,
                    options,
                    baseCharge,
                    accessorials,
                    notification,
                    subtotal,
                    accessorialsSum,
                    total,
                    invoicingNotes,
                    newNote,
                    newCharge,
                    editingOrder,
                    hasCommentErrors,
                    isBaseChargeEdited,
                    isAccessorialsEdited,
                },
                loading: {
                    init: orderInFlight || overrideInFlight,
                    update: pricingInFlight || updateInFlight || notesInFlight || upsertOrderLoading,
                },
                callbacks: {
                    setNewCharge,
                    setNotification,
                    setEditingOrder,
                    upsertOrder,
                    clearNotification: () => {
                        setNotification({});
                    },
                    setNewNote,
                    editBaseCharge: (value) => {
                        setUpdates((prev) => {
                            const clone = [...accessorials];

                            return {
                                ...prev,
                                [PERSPECTIVE.baseOverride]: value,
                                order_revenue_adjustments: {
                                    ...(updated?.order_revenue_adjustments || {}),
                                    [PERSPECTIVE.overrides]: clone,
                                },
                            };
                        });
                    },
                    editBaseChargeComment: (comment) => {
                        setBaseChargeComment(comment);
                    },
                    removeCharge: ({ idx }) => {
                        setUpdates((prev) => {
                            const clone = [...accessorials];
                            clone.splice(idx, 1);

                            return {
                                ...prev,
                                order_revenue_adjustments: {
                                    ...(updated?.order_revenue_adjustments || {}),
                                    [PERSPECTIVE.overrides]: clone,
                                },
                            };
                        });
                    },
                    editCharge: ({ idx, charge }) => {
                        const now = new Date().toISOString();
                        const userEmail = user?.email || '';
                        const existing = accessorials[idx] || {};
                        const auditedCharge = {
                            ...charge,
                            created_by: existing.created_by || userEmail,
                            created_at: existing.created_at || now,
                            last_edited_by: userEmail,
                            last_edited_at: now,
                        };
                        const clone = [...accessorials];
                        clone.splice(idx, 1, auditedCharge);

                        setUpdates((prev) => {
                            return {
                                ...prev,
                                order_revenue_adjustments: {
                                    ...(updated?.order_revenue_adjustments || {}),
                                    [PERSPECTIVE.overrides]: clone,
                                },
                            };
                        });
                    },
                    addCharge: () => {
                        const now = new Date().toISOString();
                        const userEmail = user?.email || '';
                        const auditedCharge = {
                            ...newCharge,
                            comment: newCharge?.comment || '',
                            created_by: userEmail,
                            created_at: now,
                            last_edited_by: userEmail,
                            last_edited_at: now,
                        };
                        setUpdates((prev) => {
                            const clone = [...accessorials, auditedCharge];
                            return {
                                ...prev,
                                order_revenue_adjustments: {
                                    ...(updated?.order_revenue_adjustments || {}),
                                    [PERSPECTIVE.overrides]: clone,
                                },
                            };
                        });
                        setNewCharge(null);
                    },
                    readyToInvoice: () => {
                        if (hasCommentErrors) {
                            setNotification({
                                message: 'Please add a comment to all edited rates before approving for invoicing.',
                                type: 'error',
                            });
                            return;
                        }
                        updateOrder({
                            variables: {
                                order_id: updated.order_id,
                                patch: {
                                    ready_to_invoice: true,
                                    invoicing_status: 'APPROVED',
                                },
                            },
                        });
                    },
                    saveOrder: () => {
                        if (hasCommentErrors) {
                            setNotification({
                                message: 'Please add a comment to all edited rates before saving.',
                                type: 'error',
                            });
                            return;
                        }

                        const now = new Date().toISOString();
                        const userEmail = user?.email || '';
                        const breakdown = updated?.price_breakdown?.[PERSPECTIVE.breakdown] || {};
                        const commentPatch = {
                            base_charge_comment: baseChargeComment || breakdown.base_charge_comment || '',
                            base_charge_created_by: breakdown.base_charge_created_by || userEmail,
                            base_charge_created_at: breakdown.base_charge_created_at || now,
                            base_charge_last_edited_by: userEmail,
                            base_charge_last_edited_at: now,
                        };

                        const enrichedUpdated = {
                            ...updated,
                            price_breakdown: {
                                ...(updated?.price_breakdown || {}),
                                [PERSPECTIVE.breakdown]: {
                                    ...breakdown,
                                    ...commentPatch,
                                },
                            },
                        };

                        setPricingInFlight(true);
                        post(PRICING_LTL, { [updated.order_id]: enrichedUpdated })
                            .then((resp) => {
                                const patch = pick(resp?.data?.[updated.order_id] || {}, PRICING_FIELDS);
                                updateOrder({
                                    variables: {
                                        order_id: updated.order_id,
                                        patch,
                                    },
                                }).then(() => {
                                    setUpdates({});
                                    setBaseChargeComment('');
                                });
                            })
                            .finally(() => {
                                setPricingInFlight(false);
                            });
                    },
                    saveNote: () => {
                        insertNotes({
                            variables: {
                                notes: [
                                    {
                                        order_id: updated.order_id,
                                        note: newNote,
                                        type: 'Invoicing',
                                        source_user_id: user.user_id,
                                    },
                                ],
                            },
                        }).then(() => {
                            setNewNote('');
                        });
                    },
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
